<template lang="pug">
	.creative--sonic-scribe.flex-col.space-large.audio-tracker
		CreativeTextBasedAudioEditor(v-if="track && track.__buffer && track.insertion.Audio && loadingPercentage === 100 && !busy", ref="editor", :track="track", :creative="creative", :heightAuto="true", @playing="v => isPlaying = v",
			:structureStoreMode="true", :readonly="readonly", @building="v => isBuilding = v", :sonicScribeInfo="sonicScribeInfo")
			template(slot="actions-left")
				.flex-row.space.align-center
					AudioTrackerVUMeter(v-if="tracker", ref="VUMeter", orientation="horizontal", :tickVisible="false", size="130px", thickness="4px", :isPlaying="tracker.isPlaying", :filters="tracker.masterTrackEffects.filters",
						:mono="false", :compact="true", :step="1.5")
					slot(name="actions-left")
					AudioTrackerButton(:title="$tCaption('Backward')", icon="fas fa-backward", @click="goToStart", :disabled="isBuilding", :spin="isBuilding")
					AudioTrackerButton(:title="isPlaying ? $tCaption('Stop') : $tCaption('Play')", :icon="isBuilding ? 'fas fa-spinner' : (isPlaying ? 'fas fa-stop' : 'fas fa-play')", 
						@click="isPlaying ? $refs.editor.stop() : $refs.editor.play()", :disabled="isBuilding", :spin="isBuilding")
			template(slot="actions-right")
				.flex-row.space.align-center
					slot(name="actions-right")
					AudioTrackerButton(v-if="!readonly", :text="$tCaption('Reset')", icon="fas fa-close", @click="$refs.editor.reset()")
					DxDropDownButton.no-text(
						:items="effectsList",
						@item-click="processListItem",
						display-expr="name",
						key-expr="id",
						:drop-down-options="{ width: 230 }",
						:useSelectMode="false",
						styling-mode="outlined",
						:focusStateEnabled="false",
						:class="{ 'has-changed-settings': hasAppliedCustomSettings(track, aggregatedTrack) }"
					)
						template(v-slot:header="{ data }")
							.font-tiny.upper.grey {{data.name()}}
		template(v-if="!track && !busy && !isLoading")
			.file-loader-handler.flex-col.space.align-center.center(:class="{ 'disabled-controls': loading }")
				.loader-button.flex-row.space.align-center(@click="$emit('add-file')")
					fas(icon="upload")
					span {{$tCaption('Upload file')}}
		Loader(v-if="busy || loadingPercentage < 100", :fullscreen="true", :text="$tCaption('Loading SonicScribe') + '..' + (loadingPercentage < 100 ? ` (${loadingPercentage}%)` : '')")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import CreativeTextBasedAudioEditor from './CreativeTextBasedAudioEditor.vue';
	import AudioTrackerButton from 'view/audioTracker/AudioTrackerButton.vue';
	import AudioTrackerVUMeter from 'view/audioTracker/AudioTrackerVUMeter.vue';
	import pluginsList from 'view/audioTracker/plugins/plugins-list';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';

	export default {
		translationCategory: 'creative.SonicScribe',
		name: 'creative--sonic-scribe',
		components: {
			Loader, CreativeTextBasedAudioEditor, AudioTrackerButton, AudioTrackerVUMeter, DxDropDownButton
		},
		props: ['creative', 'tracker', 'busy', 'loading', 'readonly', 'sonicScribeInfo', 'executeTrackAction', 'hasAppliedCustomSettings', 'getActiveWapiPlugin'],
		data() {
			return {
				isLoading: 0,
				track: null,
				isPlaying: false,
				//structure: null,
				isBuilding: false
			};
		},
		computed: {
			loadingPercentage() {
				return this.track?.__loadedPercentage || 100;
			},
			effectsList() {
				let plugins = pluginsList.plugins.map(p => ({
					id: `wapiPlugin_${p.id}`,
					name: p.name,
					icon: (track) => {
						let plugin = this.getActiveWapiPlugin(track.__aggregatedTrack, p.id);
						return `${p.icon || 'fas fa-microchip'} ${plugin?.enabled ? 'tracker-active-icon' : ''}`
					},
					hidden: (track) => ['video'].includes(track?.__type)
				}));

				return [
					{
						id: 'effects',
						name: () => this.$tCaption('Effects'),
						template: 'header',
						disabled: true
					},
					...plugins
						.filter(f => !f.hidden || !f.hidden(this.track, this.track.__aggregatedTrack))
						.map(f => ({ ...f, icon: typeof(f.icon) === 'function' ? f.icon(this.track, this.track.__aggregatedTrack) : f.icon })),
					{
						id: 'common',
						name: () => this.$tCaption('Common'),
						template: 'header',
						disabled: true
					},
					{
						id: `masterTrackEffects`,
						name: () => this.$auth.hasProAudio() ? this.$tCaption('Plugin and effects banks') : this.$tCaption('Effects banks'),
						icon: 'fas fa-database',
						hidden: (track) => ['video'].includes(track?.__type),
						execute: () => this.track.__tracker.$refs.masterTrack.showPostEffects(true)
					}
				]
			},
			aggregatedTrack() {
				return this.track.__aggregatedTrack;
			}
		},
		watch: {
			'busy'(v) {
				if (!v) {
					this.checkTracks(true);
				}
			},
			'loading'() {
				this.checkTracks();
			},
			loadingPercentage(v) {
				if (v && !this.track) {
					this.checkTracks();
				}
			}
		},
		mounted() {
			if (__DEBUG__)
				window.sonicScribeCreative = this;

			this.initSonicScribeKeyHandles();
		},
		destroyed() {
			this.removeSonicScribeKeyHandles();
		},
		methods: {
			goToStart() {
				this.$eventsBus.$emit('sonic-scribe-timeline', 0, true, { clearSelection: true });
			},
			processListItem(v) {
				if (v?.itemData?.execute) {
					return v.itemData.execute();
				} else {
					this.executeTrackAction(v.itemData.id, this.track, v.itemData, this.aggregatedTrack);
				}
			},
			checkTracks(emitNew) {
				this.track = (this.tracker?.tracks || [])[0];

				if (emitNew && !this.track) {
					this.$emit('add-file');
				}
			},
			isInput() {
				var activeElement = document.activeElement;
				var inputs = ['input', 'select', 'button', 'textarea'];
				var classes = activeElement ? [...activeElement.classList] : [];

				return activeElement && (inputs.indexOf(activeElement.tagName.toLowerCase()) > -1 || classes.includes('jodit-wysiwyg'));
			},
			initSonicScribeKeyHandles() {
				document.body.addEventListener('keypress', this.sonicScribeKeyPress);
			},
			removeSonicScribeKeyHandles() {
				document.body.removeEventListener('keypress', this.sonicScribeKeyPress);
			},
			sonicScribeKeyPress(e) {
				if (this._isDestroyed || this.isInput()) return;
				
				if (__DEBUG__)
					console.debug('sonicScribeKeyPress', { e });

				if (e.code === 'Space') {
					this.toggleBottomPlayer();
				}
			},
			toggleBottomPlayer() {
				let player = this.$refs.editor?.$refs?.bottomPlayer;
				let debugMarker = (this.$refs.editor?.debugMarkers || []).find(f => f.type === 'startPeak');

				if (!player || !debugMarker) return;

				if (player.isPlaying) {
					player.togglePlaying();
					player.setPosition(debugMarker?.time);
				} else {
					player.setPosition(debugMarker?.time);
					player.togglePlaying();
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.creative--sonic-scribe {
		.file-loader-handler {
			height: 60vh;
		}
		.loader-button {
			padding: 40px;
			background: #ffffff99;
			font-size: 18px;
			font-weight: 100;
			border-radius: 2px;
			transition: 0.2s;
			cursor: pointer;
			user-select: none;

			&:hover {
				background: $base-accent;
				color: $base-bg;
				transform: scale(1.05);
			}
		}
	}
</style>
